import { Plus, type LucideIcon } from 'lucide-react'

import { cn } from './cn'
import { useDisabled } from './control-scope'
import { LoadingIndicator } from './LoadingIndicator'
import { CHIP_SHAPE, FOCUS_RING } from './recipes'

/**
 * The shape the control takes in the collection it adds to.
 *
 * `row` is a bar under a stack of rows; `tile` is one more cell in a grid of
 * pictures, and takes its corner from the tiles beside it so the grid keeps
 * one radius; `chip` is one more entry in a row of filter chips, and takes its
 * box from `CHIP_SHAPE` so a full and an empty entry sit on one line.
 */
const LAYOUT = {
  row: 'w-full flex-row gap-2 rounded-lg border-2 py-2.5',
  tile: 'aspect-4/3 flex-col gap-1.5 rounded-md border',
  chip: `border ${CHIP_SHAPE}`,
} as const

/** "Add one more" where the collection it adds to is a stack or a grid. */
export function DashedAddButton({
  label,
  onClick,
  icon: Icon = Plus,
  iconSize = 14,
  layout = 'row',
  isLoading = false,
}: {
  label: string
  onClick: () => void
  /** The mark of the thing being added, where a bare plus says too little. */
  icon?: LucideIcon
  iconSize?: number
  layout?: keyof typeof LAYOUT
  /** Swaps the label for a spinner while what the press started runs. */
  isLoading?: boolean
}) {
  // A control that adds something: a surface refusing changes disables it the
  // way it disables a `Button`.
  const disabled = useDisabled(isLoading)

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center border-dashed border-border text-xs text-faint transition-colors',
        'hover:border-faint hover:bg-line-soft',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:bg-transparent',
        FOCUS_RING,
        LAYOUT[layout],
      )}
    >
      {isLoading ? (
        <LoadingIndicator layout="inline" />
      ) : (
        <>
          <Icon size={iconSize} aria-hidden />
          {label}
        </>
      )}
    </button>
  )
}
